/**
 * Unified Notification Dropdown Component
 *
 * Displays a combined view of system notifications and user messages in a dropdown.
 * Features:
 * - Visual distinction between notifications and messages
 * - Real-time updates via WebSocket
 * - Automatic mark-as-read on item click
 * - Loading states and error handling
 * - Accessibility support
 *
 * @author Andres Nava
 * @version 1.0.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
// import { logger } from "../../utils/logger";
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, Divider, CircularProgress, Alert, Button, Chip, IconButton, useTheme } from '@mui/material';
import { Notifications as NotificationsIcon, Message as MessageIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Warning as WarningIcon, Info as InfoIcon, ArrowForward as ArrowForwardIcon, Refresh as RefreshIcon, Person as PersonIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import notificationService, { UnifiedItem } from '../../services/notificationService';
import { apiClient } from '../../utils/apiClient';
import { logger } from "../../utils/logger";
import { isBackofficeHostname, toBackofficeRoutePath } from '../../pages/backoffice/backofficeRoutePaths';
interface UnifiedNotificationDropdownProps {
    onClose?: () => void;
    onItemClick?: (item: UnifiedItem) => void;
    /** Called when unread totals are refreshed so header badges stay in sync */
    onUnreadCountChange?: (unreadTotal: number) => void;
}
const UnifiedNotificationDropdown: React.FC<UnifiedNotificationDropdownProps> = ({ onClose, onItemClick, onUnreadCountChange }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState<UnifiedItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [counts, setCounts] = useState({
        unreadNotifications: 0,
        unreadMessages: 0,
        unreadTotal: 0
    });
    const isMounted = useRef(true);
    const socketRef = useRef<Socket | null>(null);
    const isBackofficeSurface = location.pathname.startsWith('/backoffice') || isBackofficeHostname();
    // Fetch unified notifications and messages
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await notificationService.getUnifiedNotifications(10);
            if (!isMounted.current)
                return;
            if (result.success && result.data) {
                setItems(result.data.items);
                const nextCounts = {
                    unreadNotifications: result.data.counts.unreadNotifications,
                    unreadMessages: result.data.counts.unreadMessages,
                    unreadTotal: result.data.counts.unreadTotal
                };
                setCounts(nextCounts);
                onUnreadCountChange?.(nextCounts.unreadTotal);
            }
            else {
                setError(result.error || 'Failed to fetch notifications');
            }
        }
        catch (err) {
            if (!isMounted.current)
                return;
            logger.error('Error fetching unified notifications:', err, {});
            setError('Failed to fetch notifications. Please try again.');
        }
        finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [onUnreadCountChange]);
    useEffect(() => {
        isMounted.current = true;
        fetchData();
        return () => {
            isMounted.current = false;
        };
    }, [fetchData]);
    // WebSocket real-time updates
    useEffect(() => {
        // Don't establish connection if not in browser or not authenticated
        if (typeof window === 'undefined')
            return;
        // Always use relative URLs to go through Vite proxy
        // In development, this goes through Vite proxy to avoid mixed content issues
        const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
        const socketUrl = isDevelopment
            ? '' // Use current origin, Vite proxy handles /socket.io requests
            : (import.meta.env.VITE_API_URL as string)?.replace('/api', '') || 'https://americanpolicetraining.com:5443';
        try {
            const socket = io(socketUrl, {
                // In development, use polling only to avoid WebSocket handshake errors through Vite proxy
                transports: isDevelopment ? ['polling'] : ['polling', 'websocket'],
                upgrade: !isDevelopment, // Disable upgrade in development
                withCredentials: true // Include cookies for session-based auth
            });
            socketRef.current = socket;
            // Handle connection
            socket.on('connect', () => {
                logger.debug('🔌 WebSocket connected for notifications');
            });
            // Handle new notification
            socket.on('notification', (data: any) => {
                logger.debug('📬 New notification received:', { "arg0": data });
                // Refresh data to get latest
                fetchData();
            });
            // Handle new message
            socket.on('message', (data: any) => {
                logger.debug('💬 New message received:', { "arg0": data });
                // Refresh data to get latest
                fetchData();
            });
            // Handle new chat message
            socket.on('new-chat-message', (data: any) => {
                logger.debug('💬 New chat message received:', { "arg0": data });
                // Refresh data to get latest
                fetchData();
            });
            // Handle errors
            socket.on('error', (error: any) => {
                // Suppress WebSocket handshake errors in development (expected when using polling only)
                const errorMessage = error?.message || '';
                const isHandshakeError = errorMessage.includes('Unexpected response code: 200') ||
                                       errorMessage.includes('Error during WebSocket handshake') ||
                                       errorMessage.includes('WebSocket handshake');
                if (!isHandshakeError || !isDevelopment) {
                    logger.error('🔴 WebSocket error:', error, {});
                }
            });
            socket.on('connect_error', (error: any) => {
                // Suppress WebSocket handshake errors in development (expected when using polling only)
                const errorMessage = error?.message || '';
                const isHandshakeError = errorMessage.includes('Unexpected response code: 200') ||
                                       errorMessage.includes('Error during WebSocket handshake') ||
                                       errorMessage.includes('WebSocket handshake');
                if (!isHandshakeError || !isDevelopment) {
                    logger.error('🔴 WebSocket connection error:', error, {});
                }
            });
            // Handle disconnect
            socket.on('disconnect', () => {
                logger.debug('🔌 WebSocket disconnected');
            });
            return () => {
                if (socketRef.current) {
                    socketRef.current.disconnect();
                    socketRef.current = null;
                }
            };
        }
        catch (error) {
            logger.error('Error establishing WebSocket connection:', error, {});
        }
    }, [fetchData]);
    // Mark item as read and navigate
    const handleItemClick = useCallback(async (item: UnifiedItem) => {
        const notificationPath = isBackofficeSurface
            ? (toBackofficeRoutePath(`/notifications?highlight=${item.sourceId}`) ??
                `/notifications?highlight=${item.sourceId}`)
            : `/notifications?highlight=${item.sourceId}`;
        const messagePath = isBackofficeSurface
            ? (toBackofficeRoutePath(`/messages?highlight=${item.sourceId}`) ??
                `/messages?highlight=${item.sourceId}`)
            : `/messages?highlight=${item.sourceId}`;
        const destination = item.sourceType === 'notification' ? notificationPath : messagePath;
        try {
            // Mark as read before navigation
            if (!item.read) {
                if (item.sourceType === 'notification') {
                    await apiClient.put(`/notifications/${item.sourceId}/read`);
                }
                else if (item.sourceType === 'message') {
                    await apiClient.put(`/messaging/messages/${item.sourceId}`, { status: 'read' });
                }
                // Update local state optimistically
                setItems(prev => prev.map(i => i.id === item.id ? { ...i, read: true } : i));
                setCounts(prev => {
                    const next = {
                        ...prev,
                        unreadTotal: Math.max(0, prev.unreadTotal - 1),
                        ...(item.sourceType === 'notification'
                            ? { unreadNotifications: Math.max(0, prev.unreadNotifications - 1) }
                            : { unreadMessages: Math.max(0, prev.unreadMessages - 1) })
                    };
                    onUnreadCountChange?.(next.unreadTotal);
                    return next;
                });
            }
            // Call custom handler if provided
            if (onItemClick) {
                onItemClick(item);
            }
            navigate(destination);
            if (onClose) {
                onClose();
            }
        }
        catch (err) {
            logger.error('Error marking item as read:', err, {});
            navigate(destination);
            if (onClose) {
                onClose();
            }
        }
    }, [navigate, onClose, onItemClick, onUnreadCountChange, isBackofficeSurface]);
    // Get icon based on type
    const getIcon = (item: UnifiedItem) => {
        if (item.sourceType === 'message') {
            return <MessageIcon color="primary"/>;
        }
        switch (item.type) {
            case 'success':
                return <CheckCircleIcon color="success"/>;
            case 'warning':
                return <WarningIcon color="warning"/>;
            case 'error':
                return <ErrorIcon color="error"/>;
            case 'info':
                return <InfoIcon color="info"/>;
            default:
                return <NotificationsIcon color="action"/>;
        }
    };
    // Get priority color
    const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'success' => {
        switch (priority) {
            case 'urgent':
                return 'error';
            case 'high':
                return 'warning';
            case 'normal':
                return 'primary';
            case 'low':
                return 'default';
            default:
                return 'default';
        }
    };
    // Render empty state
    if (!loading && items.length === 0 && !error) {
        return (<Box sx={{ p: 3, textAlign: 'center', minWidth: 350, maxWidth: 450 }}>
        <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}/>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          No notifications or messages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You're all caught up!
        </Typography>
      </Box>);
    }
    return (<Box sx={{
            minWidth: 350,
            maxWidth: 450,
            maxHeight: 600,
            display: 'flex',
            flexDirection: 'column'
        }} role="menu" aria-label="Notifications and messages">
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Notifications & Messages
        </Typography>
        <IconButton size="small" onClick={fetchData} disabled={loading} aria-label="Refresh notifications">
          <RefreshIcon />
        </IconButton>
      </Box>
      
      <Divider />

      {/* Loading State */}
      {loading && (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={32}/>
        </Box>)}

      {/* Error State */}
      {error && !loading && (<Box sx={{ p: 2 }}>
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>}>
            {error}
          </Alert>
        </Box>)}

      {/* Items List */}
      {!loading && !error && items.length > 0 && (<>
          <List sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 0,
                '& .MuiListItem-root': {
                    transition: 'background-color 0.2s',
                }
            }}>
            {items.map((item, index) => (<React.Fragment key={item.id}>
                <ListItem button onClick={() => handleItemClick(item)} sx={{
                    py: 1.5,
                    px: 2,
                    backgroundColor: item.read ? 'transparent' : 'action.hover',
                    '&:hover': {
                        backgroundColor: item.read ? 'action.hover' : 'action.selected',
                    }
                }} role="menuitem" tabIndex={0} onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(item);
                    }
                }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getIcon(item)}
                  </ListItemIcon>
                  
                  <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{
                        fontWeight: item.read ? 'normal' : 'bold',
                        flex: 1
                    }}>
                          {item.title}
                        </Typography>
                        {!item.read && (<Box sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            flexShrink: 0
                        }} aria-label="Unread"/>)}
                      </Box>} secondary={<Box>
                        <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        mb: 0.5
                    }}>
                          {item.message}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {item.sourceType === 'message' && item.metadata?.sender_first_name && (<Chip icon={<PersonIcon />} label={`${item.metadata.sender_first_name} ${item.metadata.sender_last_name || ''}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }}/>)}
                          
                          {item.priority !== 'normal' && (<Chip label={item.priority} size="small" color={getPriorityColor(item.priority)} sx={{ height: 20, fontSize: '0.7rem' }}/>)}
                          
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(parseISO(item.created_at), { addSuffix: true })}
                          </Typography>
                        </Box>
                      </Box>}/>
                </ListItem>
                {index < items.length - 1 && <Divider variant="inset" component="li"/>}
              </React.Fragment>))}
          </List>

          <Divider />

          {/* Footer with View All Links */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
            <Button fullWidth size="small" endIcon={<ArrowForwardIcon />} onClick={() => {
                navigate(isBackofficeSurface
                    ? (toBackofficeRoutePath('/notifications') ?? '/notifications')
                    : '/notifications');
                if (onClose)
                    onClose();
            }} sx={{ textTransform: 'none' }}>
              View All Notifications
              {counts.unreadNotifications > 0 && (<Chip label={counts.unreadNotifications} size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}/>)}
            </Button>
            
            <Button fullWidth size="small" endIcon={<ArrowForwardIcon />} onClick={() => {
                navigate(isBackofficeSurface
                    ? (toBackofficeRoutePath('/messages') ?? '/messages')
                    : '/messages');
                if (onClose)
                    onClose();
            }} sx={{ textTransform: 'none' }}>
              View All Messages
              {counts.unreadMessages > 0 && (<Chip label={counts.unreadMessages} size="small" color="error" sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}/>)}
            </Button>
          </Box>
        </>)}
    </Box>);
};
export default UnifiedNotificationDropdown;
